import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Alert, Typography, Flex, Image, message } from "antd";
import {
  CameraOutlined,
  ReloadOutlined,
  CheckCircleFilled,
  DeleteOutlined,
} from "@ant-design/icons";
import CustomButton from "../CustomButton/Button";
import CustomModal from "../CustomModal/CustomModal";
import PrimaryButton from "../CustomButton/PrimaryButton";
import "./LiveSelfieCapture.scss";
import CancelButton from "../CustomButton/CancelButton";

const { Text } = Typography;

const MIN_BRIGHTNESS = 55;
const MAX_BRIGHTNESS = 210;
const MIN_SKIN_RATIO = 0.08;
const MIN_CONTRAST = 18;
const ANALYSIS_INTERVAL_MS = 250;

// Oval guide proportions relative to the video wrapper (matches SCSS)
const OVAL_WIDTH_RATIO = 220 / 440;
const OVAL_HEIGHT_RATIO = 270 / 330;

const getFaceDetector = () => {
  if (typeof window === "undefined" || typeof window.FaceDetector !== "function") {
    return null;
  }
  try {
    return new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
  } catch {
    return null;
  }
};

const isPointInOval = (x, y, cx, cy, rx, ry) => {
  const dx = (x - cx) / rx;
  const dy = (y - cy) / ry;
  return dx * dx + dy * dy <= 1;
};

const isSkinTone = (r, g, b) => {
  // Simple YCbCr skin-tone heuristic for framing feedback
  const y = 0.299 * r + 0.587 * g + 0.114 * b;
  const cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
  const cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
  return y > 40 && y < 230 && cb > 77 && cb < 127 && cr > 133 && cr < 173;
};

const analyzeFrameQuality = (video, canvas, faceDetector) => {
  if (!video || !canvas || video.readyState < 2 || !video.videoWidth) {
    return {
      canCapture: false,
      brightnessOk: false,
      faceInFrame: false,
      message: "Waiting for camera...",
    };
  }

  const width = video.videoWidth;
  const height = video.videoHeight;
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(video, 0, 0, width, height);

  const cx = width / 2;
  const cy = height / 2;
  const rx = (width * OVAL_WIDTH_RATIO) / 2;
  const ry = (height * OVAL_HEIGHT_RATIO) / 2;

  const sampleLeft = Math.max(0, Math.floor(cx - rx));
  const sampleTop = Math.max(0, Math.floor(cy - ry));
  const sampleWidth = Math.min(width - sampleLeft, Math.ceil(rx * 2));
  const sampleHeight = Math.min(height - sampleTop, Math.ceil(ry * 2));
  const imageData = ctx.getImageData(sampleLeft, sampleTop, sampleWidth, sampleHeight);
  const { data } = imageData;

  const sampleStep = Math.max(4, Math.floor(Math.min(sampleWidth, sampleHeight) / 80));
  let brightnessSum = 0;
  let contrastSum = 0;
  let sampleCount = 0;
  let skinCount = 0;

  for (let y = 0; y < sampleHeight; y += sampleStep) {
    for (let x = 0; x < sampleWidth; x += sampleStep) {
      const absX = sampleLeft + x;
      const absY = sampleTop + y;
      if (!isPointInOval(absX, absY, cx, cy, rx, ry)) continue;

      const idx = (y * sampleWidth + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

      brightnessSum += brightness;
      contrastSum += Math.abs(r - g) + Math.abs(g - b) + Math.abs(r - b);
      sampleCount += 1;
      if (isSkinTone(r, g, b)) skinCount += 1;
    }
  }

  if (sampleCount === 0) {
    return {
      canCapture: false,
      brightnessOk: false,
      faceInFrame: false,
      message: "Unable to analyze camera frame.",
    };
  }

  const avgBrightness = brightnessSum / sampleCount;
  const avgContrast = contrastSum / sampleCount;
  const skinRatio = skinCount / sampleCount;

  let brightnessOk = avgBrightness >= MIN_BRIGHTNESS && avgBrightness <= MAX_BRIGHTNESS;
  let faceInFrame = false;

  // Prefer native FaceDetector when available for better framing accuracy
  if (faceDetector) {
    return faceDetector
      .detect(canvas)
      .then((faces) => {
        if (faces?.length) {
          const box = faces[0].boundingBox;
          const faceCx = box.x + box.width / 2;
          const faceCy = box.y + box.height / 2;
          const centerOk = isPointInOval(faceCx, faceCy, cx, cy, rx * 0.9, ry * 0.9);
          const sizeOk =
            box.width >= width * 0.18 &&
            box.width <= width * 0.7 &&
            box.height >= height * 0.22 &&
            box.height <= height * 0.85;
          faceInFrame = centerOk && sizeOk;
        }

        return buildQualityResult(brightnessOk, faceInFrame, avgBrightness);
      })
      .catch(() => {
        faceInFrame = skinRatio >= MIN_SKIN_RATIO && avgContrast >= MIN_CONTRAST;
        return buildQualityResult(brightnessOk, faceInFrame, avgBrightness);
      });
  }

  faceInFrame = skinRatio >= MIN_SKIN_RATIO && avgContrast >= MIN_CONTRAST;
  return Promise.resolve(buildQualityResult(brightnessOk, faceInFrame, avgBrightness));
};

const buildQualityResult = (brightnessOk, faceInFrame, avgBrightness) => {
  let message = "Looking good — you can capture now.";
  if (!brightnessOk && avgBrightness < MIN_BRIGHTNESS) {
    message = "Lighting is too dull. Move to a brighter area.";
  } else if (!brightnessOk && avgBrightness > MAX_BRIGHTNESS) {
    message = "Too much light / glare. Soften the lighting.";
  } else if (!faceInFrame) {
    message = "Center your face inside the oval frame.";
  }

  return {
    canCapture: brightnessOk && faceInFrame,
    brightnessOk,
    faceInFrame,
    message,
  };
};

const LiveSelfieCapture = ({
  value,
  onChange,
  label = "Selfie Photo",
  required = true,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedDataUrl, setCapturedDataUrl] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [previewFile, setPreviewFile] = useState(value || null);
  const [quality, setQuality] = useState({
    canCapture: false,
    brightnessOk: false,
    faceInFrame: false,
    message: "Position your face in the frame.",
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const analysisCanvasRef = useRef(null);
  const faceDetectorRef = useRef(null);
  const analysisTimerRef = useRef(null);
  const analyzingRef = useRef(false);

  const startCamera = async () => {
    setCameraError(null);
    setCapturedDataUrl(null);
    setQuality({
      canCapture: false,
      brightnessOk: false,
      faceInFrame: false,
      message: "Starting camera...",
    });
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Camera access error:", err);
      setCameraError(
        "Camera permission denied or camera not found. Please enable camera access in your browser settings.",
      );
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const stopQualityAnalysis = useCallback(() => {
    if (analysisTimerRef.current) {
      clearInterval(analysisTimerRef.current);
      analysisTimerRef.current = null;
    }
    analyzingRef.current = false;
  }, []);

  const runQualityAnalysis = useCallback(async () => {
    if (analyzingRef.current || capturedDataUrl || cameraError) return;
    analyzingRef.current = true;
    try {
      if (!analysisCanvasRef.current) {
        analysisCanvasRef.current = document.createElement("canvas");
      }
      if (!faceDetectorRef.current) {
        faceDetectorRef.current = getFaceDetector();
      }
      const result = await analyzeFrameQuality(
        videoRef.current,
        analysisCanvasRef.current,
        faceDetectorRef.current,
      );
      setQuality(result);
    } catch (err) {
      console.error("Selfie quality analysis error:", err);
    } finally {
      analyzingRef.current = false;
    }
  }, [capturedDataUrl, cameraError]);

  const startQualityAnalysis = useCallback(() => {
    stopQualityAnalysis();
    analysisTimerRef.current = setInterval(runQualityAnalysis, ANALYSIS_INTERVAL_MS);
  }, [runQualityAnalysis, stopQualityAnalysis]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (isModalOpen && !capturedDataUrl) {
      startCamera();
    }
    return () => {
      stopCamera();
      stopQualityAnalysis();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen && stream && !capturedDataUrl && !cameraError) {
      startQualityAnalysis();
    } else {
      stopQualityAnalysis();
    }
    return stopQualityAnalysis;
  }, [
    isModalOpen,
    stream,
    capturedDataUrl,
    cameraError,
    startQualityAnalysis,
    stopQualityAnalysis,
  ]);

  const handleCapture = () => {
    if (!quality.canCapture) return;
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    const ctx = canvas.getContext("2d");
    // Mirror the image horizontally to match webcam feel
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setCapturedDataUrl(dataUrl);
    stopQualityAnalysis();
    stopCamera();
  };

  const handleRetake = () => {
    setCapturedDataUrl(null);
    startCamera();
  };

  const handleConfirmSelfie = () => {
    if (!capturedDataUrl) return;

    // Convert dataURL to File object
    const arr = capturedDataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    const file = new File([u8arr], `selfie_${Date.now()}.jpg`, { type: mime });

    setPreviewFile({
      file,
      url: capturedDataUrl,
    });

    if (onChange) {
      onChange(file);
    }

    setIsModalOpen(false);
    stopCamera();
    message.success("Selfie captured successfully!");
  };

  const handleRemove = () => {
    setPreviewFile(null);
    setCapturedDataUrl(null);
    if (onChange) {
      onChange(null);
    }
  };

  const handleCloseModal = () => {
    stopQualityAnalysis();
    stopCamera();
    setIsModalOpen(false);
    setCapturedDataUrl(null);
  };

  const guideStatusClass = quality.canCapture
    ? "guide-ok"
    : !quality.brightnessOk
      ? "guide-dark"
      : "guide-frame";

  return (
    <div className="live-selfie-capture">
      <label className={`selfie-label ${required ? "required-label" : ""}`}>
        {label}
      </label>

      {previewFile ? (
        <div className="selfie-preview-card">
          <Image
            src={
              previewFile.url ||
              (previewFile.file ? URL.createObjectURL(previewFile.file) : "")
            }
            alt="Captured Selfie"
            className="selfie-thumbnail"
            width={120}
            height={120}
            style={{ objectFit: "cover", borderRadius: "50%" }}
          />
          <Flex gap={10} align="center" style={{ marginTop: 10 }}>
            <Text type="success">
              <CheckCircleFilled style={{ color: "#52c41a", marginRight: 6 }} />
              Live Selfie Captured
            </Text>
            <CustomButton
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
            >
              Remove / Retake
            </CustomButton>
          </Flex>
        </div>
      ) : (
        <div className="selfie-trigger">
          <Button
            type="primary"
            icon={<CameraOutlined />}
            onClick={handleOpenModal}
            size="large"
            className="take-selfie-btn"
          >
            Take Live Selfie
          </Button>
          <Text
            type="secondary"
            style={{ display: "block", marginTop: 4, fontSize: 12 }}
          >
            You must capture a live photo using your device camera.
          </Text>
        </div>
      )}

      {/* Hidden canvas for capturing video frame */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Selfie Modal */}
      <CustomModal
        open={isModalOpen}
        onCancel={handleCloseModal}
        title="Take Live Selfie"
        width={540}
        content={
          <div className="selfie-modal-content">
            {cameraError ? (
              <Alert
                message="Camera Error"
                description={cameraError}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            ) : capturedDataUrl ? (
              <div className="captured-preview-container">
                <img
                  src={capturedDataUrl}
                  alt="Captured Selfie"
                  className="captured-img"
                />
                <Flex justify="center" gap={12} style={{ marginTop: 20 }}>
                  <CustomButton icon={<ReloadOutlined />} onClick={handleRetake}>
                    Retake Photo
                  </CustomButton>
                  <CustomButton type="primary" onClick={handleConfirmSelfie}>
                    Use This Photo
                  </CustomButton>
                </Flex>
              </div>
            ) : (
              <div className="video-stream-container">
                <div className="video-wrapper">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="video-element"
                  />
                  <div
                    className={`face-guide-overlay ${guideStatusClass}`}
                  />
                </div>
                <Text
                  className={`face-guide-text ${
                    quality.canCapture ? "text-ok" : "text-warn"
                  }`}
                >
                  {quality.message}
                </Text>
                <Flex justify="center" gap={12} style={{ marginTop: 16 }}>
                  <CancelButton onClick={handleCloseModal} />
                  <PrimaryButton
                    icon={<CameraOutlined />}
                    onClick={handleCapture}
                    disabled={!quality.canCapture}
                  >
                    Capture Photo
                  </PrimaryButton>
                </Flex>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default LiveSelfieCapture;
